import { createTheme, ThemeOptions } from '@mui/material/styles';

// Augment the MUI theme to include custom palette colors
declare module '@mui/material/styles' {
  interface Palette {
    custom: {
      softBackground: string;
      softBorder: string;
      primaryTint: string;
      primaryTintHover: string;
      primaryTintBorder: string;
      infoTint: string;
      infoTintHover: string;
      successTint: string;
      successTintHover: string;
      warningTint: string;
      warningTintHover: string;
      errorTint: string;
      errorTintHover: string;
      inputBackground: string;
    };
  }
  interface PaletteOptions {
    custom?: {
      softBackground: string;
      softBorder: string;
      primaryTint: string;
      primaryTintHover: string;
      primaryTintBorder: string;
      infoTint: string;
      infoTintHover: string;
      successTint: string;
      successTintHover: string;
      warningTint: string;
      warningTintHover: string;
      errorTint: string;
      errorTintHover: string;
      inputBackground: string;
    };
  }
}

// SimplyPayroll Brand Colors (Updated Dec 2025)
const brandColors = {
  primary: {
    main: '#1E23FE',
    light: '#5C60FE',
    dark: '#1B35E9',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#0D9E6F',
    light: '#0CA963',
    dark: '#087A54',
    contrastText: '#ffffff',
  },
  success: {
    main: '#0CA963',
    light: '#10C977',
    dark: '#087A54',
  },
  error: {
    main: '#D32F2F',
    light: '#EF5350',
    dark: '#C62828',
  },
  warning: {
    main: '#FF9800',
    light: '#FFB74D',
    dark: '#F57C00',
  },
  info: {
    main: '#1374A1',
    light: '#1A8FC4',
    dark: '#0E5A7D',
  },
};

// Custom colors that adapt to light/dark mode
const getCustomColors = (mode: 'light' | 'dark') => ({
  // Soft backgrounds for highlights/sections
  softBackground: mode === 'light' ? '#f5f7f5' : '#252525',
  softBorder:
    mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)',
  // Primary tints for backgrounds
  primaryTint:
    mode === 'light' ? 'rgba(30, 35, 254, 0.08)' : 'rgba(30, 35, 254, 0.15)',
  primaryTintHover:
    mode === 'light' ? 'rgba(30, 35, 254, 0.12)' : 'rgba(30, 35, 254, 0.22)',
  primaryTintBorder:
    mode === 'light' ? 'rgba(30, 35, 254, 0.2)' : 'rgba(30, 35, 254, 0.35)',
  // Info tints for backgrounds
  infoTint:
    mode === 'light' ? 'rgba(19, 116, 161, 0.08)' : 'rgba(19, 116, 161, 0.15)',
  infoTintHover:
    mode === 'light' ? 'rgba(19, 116, 161, 0.12)' : 'rgba(19, 116, 161, 0.22)',
  // Success tints for backgrounds
  successTint:
    mode === 'light' ? 'rgba(12, 169, 99, 0.08)' : 'rgba(12, 169, 99, 0.15)',
  successTintHover:
    mode === 'light' ? 'rgba(12, 169, 99, 0.12)' : 'rgba(12, 169, 99, 0.22)',
  // Warning tints for backgrounds
  warningTint:
    mode === 'light' ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.15)',
  warningTintHover:
    mode === 'light' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.22)',
  // Error tints for backgrounds
  errorTint:
    mode === 'light' ? 'rgba(211, 47, 47, 0.08)' : 'rgba(211, 47, 47, 0.15)',
  errorTintHover:
    mode === 'light' ? 'rgba(211, 47, 47, 0.12)' : 'rgba(211, 47, 47, 0.22)',
  // Input backgrounds
  inputBackground: mode === 'light' ? '#ffffff' : '#2a2a2a',
});

const getDesignTokens = (mode: 'light' | 'dark'): ThemeOptions => {
  const customColors = getCustomColors(mode);

  return {
    palette: {
      mode,
      primary: brandColors.primary,
      secondary: brandColors.secondary,
      success: brandColors.success,
      error: brandColors.error,
      warning: brandColors.warning,
      info: brandColors.info,
      background: {
        default: mode === 'light' ? '#f8faf8' : '#121212',
        paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
      },
      text: {
        primary: mode === 'light' ? '#1a1a1a' : '#f5f5f5',
        secondary: mode === 'light' ? '#555555' : '#b0b0b0',
      },
      divider:
        mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
      // Custom colors for dark mode compatibility
      custom: customColors,
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
      h1: {
        fontWeight: 700,
        fontSize: '2.5rem',
        letterSpacing: '-0.02em',
      },
      h2: {
        fontWeight: 700,
        fontSize: '2rem',
        letterSpacing: '-0.01em',
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.75rem',
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.5rem',
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.25rem',
      },
      h6: {
        fontWeight: 600,
        fontSize: '1rem',
      },
      subtitle1: {
        fontWeight: 500,
        fontSize: '1rem',
      },
      subtitle2: {
        fontWeight: 500,
        fontSize: '0.875rem',
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      button: {
        fontWeight: 600,
        textTransform: 'none' as const,
        letterSpacing: '0.02em',
      },
    },
    shape: {
      borderRadius: 12,
    },
    shadows: [
      'none',
      '0px 1px 3px rgba(0, 0, 0, 0.04), 0px 1px 2px rgba(0, 0, 0, 0.06)',
      '0px 2px 4px rgba(0, 0, 0, 0.04), 0px 1px 3px rgba(0, 0, 0, 0.08)',
      '0px 4px 6px rgba(0, 0, 0, 0.04), 0px 2px 4px rgba(0, 0, 0, 0.06)',
      '0px 6px 8px rgba(0, 0, 0, 0.04), 0px 3px 6px rgba(0, 0, 0, 0.06)',
      '0px 8px 12px rgba(0, 0, 0, 0.04), 0px 4px 8px rgba(0, 0, 0, 0.06)',
      '0px 12px 16px rgba(0, 0, 0, 0.04), 0px 6px 12px rgba(0, 0, 0, 0.06)',
      '0px 16px 24px rgba(0, 0, 0, 0.06), 0px 8px 16px rgba(0, 0, 0, 0.04)',
      '0px 20px 28px rgba(0, 0, 0, 0.06), 0px 10px 20px rgba(0, 0, 0, 0.04)',
      '0px 24px 32px rgba(0, 0, 0, 0.06), 0px 12px 24px rgba(0, 0, 0, 0.04)',
      '0px 28px 36px rgba(0, 0, 0, 0.06), 0px 14px 28px rgba(0, 0, 0, 0.04)',
      '0px 32px 40px rgba(0, 0, 0, 0.06), 0px 16px 32px rgba(0, 0, 0, 0.04)',
      '0px 36px 44px rgba(0, 0, 0, 0.06), 0px 18px 36px rgba(0, 0, 0, 0.04)',
      '0px 40px 48px rgba(0, 0, 0, 0.06), 0px 20px 40px rgba(0, 0, 0, 0.04)',
      '0px 44px 52px rgba(0, 0, 0, 0.06), 0px 22px 44px rgba(0, 0, 0, 0.04)',
      '0px 48px 56px rgba(0, 0, 0, 0.06), 0px 24px 48px rgba(0, 0, 0, 0.04)',
      '0px 52px 60px rgba(0, 0, 0, 0.06), 0px 26px 52px rgba(0, 0, 0, 0.04)',
      '0px 56px 64px rgba(0, 0, 0, 0.06), 0px 28px 56px rgba(0, 0, 0, 0.04)',
      '0px 60px 68px rgba(0, 0, 0, 0.06), 0px 30px 60px rgba(0, 0, 0, 0.04)',
      '0px 64px 72px rgba(0, 0, 0, 0.06), 0px 32px 64px rgba(0, 0, 0, 0.04)',
      '0px 68px 76px rgba(0, 0, 0, 0.06), 0px 34px 68px rgba(0, 0, 0, 0.04)',
      '0px 72px 80px rgba(0, 0, 0, 0.06), 0px 36px 72px rgba(0, 0, 0, 0.04)',
      '0px 76px 84px rgba(0, 0, 0, 0.06), 0px 38px 76px rgba(0, 0, 0, 0.04)',
      '0px 80px 88px rgba(0, 0, 0, 0.06), 0px 40px 80px rgba(0, 0, 0, 0.04)',
      '0px 84px 92px rgba(0, 0, 0, 0.06), 0px 42px 84px rgba(0, 0, 0, 0.04)',
    ] as any,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: mode === 'light' ? '#c1c1c1' : '#555',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: mode === 'light' ? '#a8a8a8' : '#777',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '10px 20px',
            fontSize: '0.9375rem',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0px 2px 8px rgba(76, 175, 80, 0.25)',
            },
          },
          contained: {
            '&:hover': {
              boxShadow: '0px 4px 12px rgba(30, 35, 254, 0.3)',
            },
          },
          containedPrimary: {
            background: '#1E23FE',
            '&:hover': {
              background: '#1B35E9',
            },
          },
          outlined: {
            borderWidth: '1.5px',
            '&:hover': {
              borderWidth: '1.5px',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow:
              mode === 'light'
                ? '0px 4px 20px rgba(0, 0, 0, 0.05)'
                : '0px 4px 20px rgba(0, 0, 0, 0.3)',
            border: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.05)' : 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          elevation1: {
            boxShadow:
              mode === 'light'
                ? '0px 2px 8px rgba(0, 0, 0, 0.04)'
                : '0px 2px 8px rgba(0, 0, 0, 0.2)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background:
              mode === 'light'
                ? 'linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)'
                : 'linear-gradient(135deg, #1B35E9 0%, #087A54 100%)',
            boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight:
              mode === 'light' ? '1px solid rgba(0, 0, 0, 0.06)' : 'none',
            background: mode === 'light' ? '#ffffff' : '#1a1a1a',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '4px 8px',
            '&.Mui-selected': {
              backgroundColor:
                mode === 'light'
                  ? 'rgba(30, 35, 254, 0.12)'
                  : 'rgba(30, 35, 254, 0.2)',
              '&:hover': {
                backgroundColor:
                  mode === 'light'
                    ? 'rgba(30, 35, 254, 0.18)'
                    : 'rgba(30, 35, 254, 0.28)',
              },
            },
            '&:hover': {
              backgroundColor:
                mode === 'light'
                  ? 'rgba(0, 0, 0, 0.04)'
                  : 'rgba(255, 255, 255, 0.05)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: brandColors.primary.main,
                borderWidth: 2,
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            background: 'linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontWeight: 600,
              backgroundColor: mode === 'light' ? '#f5f7f5' : '#252525',
              color: mode === 'light' ? '#1a1a1a' : '#f5f5f5',
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor:
                mode === 'light'
                  ? 'rgba(76, 175, 80, 0.04)'
                  : 'rgba(76, 175, 80, 0.08)',
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 8,
            fontSize: '0.8125rem',
          },
        },
      },
    },
  };
};

export const createAppTheme = (mode: 'light' | 'dark') =>
  createTheme(getDesignTokens(mode));

// Export custom colors helper for components that need direct access
export { getCustomColors };

export default createAppTheme;
